import { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { type ColumnDef, type VisibilityState } from "@tanstack/react-table";
import {
  Button,
  Input,
  Pagination,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { sortPeriods } from "@/features/S2Dashboard/model/utils";
import { R1DataTable } from "../components/R1DataTable";
import { useR1DashboardContext } from "../context/R1DashboardProvider";
import { buildR1DownloadUrl, r1Api } from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";

function buildMastersheetUrl(period: string | null): string {
  const params = new URLSearchParams();
  if (period) {
    const quarterMatch = /^Q([1-4])\s+(\d{4})$/i.exec(period.trim());
    if (quarterMatch) {
      const quarter = Number(quarterMatch[1]);
      const year = Number(quarterMatch[2]);
      const month = quarter * 3;
      const lastDay = new Date(year, month, 0).getDate();
      params.set("scope_mode", "reference");
      params.set(
        "reference_key",
        `${year}${String(month).padStart(2, "0")}${String(lastDay).padStart(2, "0")}`,
      );
    }
    params.append("reporting_date_equals", period);
  }
  return buildR1DownloadUrl("r1_mastersheet", params);
}

// ─── Row type ─────────────────────────────────────────────────────────────────
type MastersheetRow = Record<string, unknown>;

// ─── Search ───────────────────────────────────────────────────────────────────
interface MastersheetSearch {
  companyName: string;
  fundName: string;
  fundGpId: string;
  gpIdPc: string;
  issuerId: string;
}

const EMPTY_SEARCH: MastersheetSearch = {
  companyName: "",
  fundName: "",
  fundGpId: "",
  gpIdPc: "",
  issuerId: "",
};

function SearchPopover({
  search,
  onChange,
}: {
  search: MastersheetSearch;
  onChange: (s: MastersheetSearch) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [local, setLocal] = useState<MastersheetSearch>(search);
  const hasFilter = Object.values(search).some(Boolean);

  const submit = () => onChange(local);
  const clear = () => { setLocal(EMPTY_SEARCH); onChange(EMPTY_SEARCH); };
  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter") submit(); };

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom-end" showArrow offset={10}>
      <PopoverTrigger>
        <Button color={hasFilter ? "secondary" : "primary"} size="sm" radius="full">
          Search
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="flex flex-col gap-3 p-3 w-72" onKeyDown={onKey}>
          <Input size="sm" label="Company name" placeholder="Name, brand, old names…"
            value={local.companyName} onValueChange={(v) => setLocal((s) => ({ ...s, companyName: v }))} />
          <Input size="sm" label="Fund name" placeholder="Fund name…"
            value={local.fundName} onValueChange={(v) => setLocal((s) => ({ ...s, fundName: v }))} />
          <Input size="sm" label="Fund GP ID" placeholder="GP ID Fund…"
            value={local.fundGpId} onValueChange={(v) => setLocal((s) => ({ ...s, fundGpId: v }))} />
          <Input size="sm" label="GP ID PC" placeholder="GP ID PC…"
            value={local.gpIdPc} onValueChange={(v) => setLocal((s) => ({ ...s, gpIdPc: v }))} />
          <Input size="sm" label="Issuer ID" placeholder="Exact issuer ID…"
            value={local.issuerId} onValueChange={(v) => setLocal((s) => ({ ...s, issuerId: v }))} />
          <div className="flex gap-2">
            <Button size="sm" color="primary" className="flex-1" onPress={submit}>Search</Button>
            {hasFilter && (
              <Button size="sm" variant="flat" onPress={clear}>Clear</Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function splitTags(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v).trim()).filter(Boolean);
      }
    } catch {
      // keep CSV fallback
    }
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
}

// ─── Formatters ───────────────────────────────────────────────────────────────
const t = (key: string, size = 120): ColumnDef<MastersheetRow> => ({
  accessorKey: key,
  size,
  meta: { variant: "default" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    return String(v);
  },
});

const num = (
  key: string,
  header: string,
  size = 110,
): ColumnDef<MastersheetRow> => ({
  accessorKey: key,
  header,
  size,
  meta: { variant: "numeric" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
      n,
    );
  },
});

const pct = (
  key: string,
  header: string,
  size = 90,
): ColumnDef<MastersheetRow> => ({
  accessorKey: key,
  header,
  size,
  meta: { variant: "numeric" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v));
    if (isNaN(n)) return String(v);
    return (n * 100).toFixed(2) + "%";
  },
});

const bool = (
  key: string,
  header: string,
  size = 85,
): ColumnDef<MastersheetRow> => ({
  accessorKey: key,
  header,
  size,
  meta: { variant: "boolean" },
  cell: ({ getValue }) => {
    const v = getValue();
    if (v === null || v === undefined) return "";
    if (v === true || v === "true" || v === "WAHR" || v === "Yes")
      return (
        <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-emerald-700">
          Yes
        </span>
      );
    if (v === false || v === "false" || v === "FALSCH" || v === "No")
      return (
        <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium leading-4 text-slate-600">
          No
        </span>
      );
    return String(v);
  },
});

// ─── Default column visibility ─────────────────────────────────────────────────
// Empty visibility map => all columns visible by default.
const COLUMN_VISIBILITY_INIT: VisibilityState = {};

// ─── Rich cell helpers ────────────────────────────────────────────────────────
function RichCell({
  primary,
  secondary,
}: {
  primary: string;
  secondary?: string;
}) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[13px] font-normal text-blue-600/85">
        {primary || "—"}
      </div>
      {secondary && (
        <div className="truncate text-[11px] text-slate-500">{secondary}</div>
      )}
    </div>
  );
}

// ─── 51 Mastersheet columns — ordered to match the golden Excel (Portfolio_VC) ──
const columns: ColumnDef<MastersheetRow>[] = [
  // 1  GP-ID PC
  { ...t("r1_gp_id_pc", 100), header: "GP-ID PC" },
  // 2  Reporting Date (BERICHTSSTICHTAG)
  { ...t("reporting_date", 105), header: "Reporting Date" },
  // 3  GP-ID Fund (GP-ID Fonds)
  { ...t("r1_gp_id_fonds", 100), header: "GP-ID Fund" },
  // 4  Fund (Fonds) — rich cell: name + latest reporting period
  {
    accessorKey: "fund_name",
    header: "Fund",
    size: 180,
    meta: { variant: "identity" },
    cell: ({ row, getValue }) => (
      <RichCell
        primary={String(getValue() ?? "")}
        secondary={
          (row.original.latest_reporting_period as string | undefined) ??
          undefined
        }
      />
    ),
  },
  // 5  Fund Currency (FONDSWÄHRUNG)
  { ...t("fund_currency", 90), header: "Fund Currency" },
  // 6  PC Name (GP-Datenbank) — rich cell: long name + investment type
  {
    accessorKey: "company_name_long",
    header: "PC Name",
    size: 170,
    meta: { variant: "identity" },
    cell: ({ row, getValue }) => (
      <RichCell
        primary={String(getValue() ?? "")}
        secondary={(row.original.asset_type as string | undefined) ?? undefined}
      />
    ),
  },
  // 7  Type of investment
  { ...t("asset_type", 160), header: "Type of investment" },
  // 8  PC new in Total Portfolio? (PCneuinGesamtportfolio?)
  { ...bool("pc_new_in_total_portfolio", "PC new in Total Portfolio?", 90) },
  // 9  PC new in Fund Portfolio? (PCneuinFondsportfolio?)
  { ...bool("pc_new_in_fund_portfolio", "PC new in Fund Portfolio?", 90) },
  // 10 Date PC new in R1 Capital Fund Portfolio
  {
    ...t("date_pc_new_in_r1_fund_portfolio", 170),
    header: "Date PC new in R1 Capital Fund Portfolio",
  },
  // 11 PC Name (eFront) — stored in brand_name
  { ...t("brand_name", 150), header: "PC Name (eFront)" },
  // 12 Legal Form (Rechtsform)
  { ...t("legal_form", 130), header: "Legal Form" },
  // 13 Country of Domicile (SITZLAND)
  { ...t("country_of_domicile", 120), header: "Country of Domicile" },
  // 14 Connection to Germany
  {
    ...bool("company_with_connection_to_germany", "Connection to Germany", 90),
  },
  // 15 Currency (Währung)
  { ...t("investment_currency", 90), header: "Currency" },
  // 16 Sector
  { ...t("sector", 120), header: "Sector" },
  // 17 Vertical
  { ...t("vertical", 140), header: "Vertical" },
  // 18 NACE Code
  { ...t("nace_code", 100), header: "NACE Code" },
  // 19 INVESTED COST (INVESTEDCOST)
  { ...num("total_investment_cost", "Invested Cost", 110) },
  // 20 PROCEEDS
  { ...num("proceeds", "Proceeds", 105) },
  // 21 REALIZED COST (REALIZEDCOST)
  { ...num("realized_cost", "Realized Cost", 110) },
  // 22 Dividends/Income
  { ...num("dividends_income", "Dividends/Income", 120) },
  // 23 Fair Value (unrealized)
  { ...num("fair_value", "Fair Value", 110) },
  // 24 Stake % non diluted
  { ...pct("stake_non_diluted", "Stake % (non diluted)", 120) },
  // 25 Stake % fully diluted
  { ...pct("stake_fully_diluted", "Stake % (fully diluted)", 120) },
  // 26 Aktive Anschaffungskosten (not in DB)
  {
    accessorKey: "_cap_acq_costs",
    header: "Cap. Acquisition Costs",
    size: 150,
    meta: { variant: "default" },
    cell: () => "",
  },
  // 27 Financial Company (Finanzunternehmen ja/nein)
  { ...bool("financial_company", "Financial Company", 90) },
  // 28 PC Description (PC Beschreibung)
  { ...t("pc_description", 170), header: "PC Description" },
  // 29 Is listed? (Ist die PC Börsennotiert)
  { ...bool("is_listed", "Is listed?", 85) },
  // 30 Was renamed? (Wurde die PC umbenannt)
  { ...bool("was_renamed", "Was renamed?", 90) },
  // 31 Old Name(s) (Alte/r Name/n)
  {
    accessorKey: "old_names",
    header: "Old Name(s)",
    size: 160,
    meta: { variant: "default" },
    cell: ({ getValue }) => {
      const v = getValue();
      if (v === null || v === undefined) return "";
      const s = typeof v === "string" ? v : JSON.stringify(v);
      return s === "[]" ? "" : s;
    },
  },
  // 32 Seed Pool
  { ...t("seed_pool", 140), header: "Seed Pool" },
  // 33 Look-through (Durchschaubar?)
  { ...bool("look_through", "Look-through", 90) },
  // 34 Comment (Kommentar)
  { ...t("comment", 160), header: "Comment" },
  // 35 LEI Code
  { ...t("lei_code", 120), header: "LEI Code" },
  // 36 First investment date
  { ...t("first_investment_date", 110), header: "First investment date" },
  // 37 Exit date
  { ...t("exit_date", 105), header: "Exit date" },
  // 38 Company Investment State
  { ...t("company_investment_state", 150), header: "Investment State" },
  // 39 Exit method
  { ...t("exit_method", 110), header: "Exit method" },
  // 40 RL Number (bei börsennotierten PC Nummer von RL)
  { ...t("r1_rl_number", 110), header: "RL Number" },
  // 41 Stage at initial investment
  { ...t("stage_at_initial_investment", 140), header: "Stage (initial)" },
  // 42 Last investment stage
  { ...t("last_investment_stage", 130), header: "Stage (last)" },
  // 43 Turnover
  { ...num("turnover", "Turnover", 110) },
  // 44 IRR
  { ...pct("irr", "IRR", 90) },
  // 45 City (City of the legal country)
  { ...t("city", 100), header: "City" },
  // 46 Unique Identifier (Eindeutiger Identifier)
  { ...t("r1_unique_identifier", 120), header: "Unique ID" },
  // 47 Tags 1
  { ...t("tags_1", 140), header: "Tags 1" },
  // 48 Tags 2
  { ...t("tags_2", 140), header: "Tags 2" },
  // 49 Tags 3
  { ...t("tags_3", 140), header: "Tags 3" },
  // 50 Tags 4
  { ...t("tags_4", 140), header: "Tags 4" },
  // 51 Tags 5
  { ...t("tags_5", 140), header: "Tags 5" },
  // 52 Tags 6
  { ...t("tags_6", 140), header: "Tags 6" },
  // 53 Tags 7
  { ...t("tags_7", 140), header: "Tags 7" },
  // 54 Tags 8
  { ...t("tags_8", 140), header: "Tags 8" },
  // 55 DRS
  { ...bool("drs", "DRS", 80) },
  // 56 Voting Rights
  { ...pct("voting_rights", "Voting Rights", 100) },
  // 57 Spare Column 1
  { ...t("spare_1", 100), header: "Spare 1" },
  // 58 Spare Column 2
  { ...t("spare_2", 100), header: "Spare 2" },
];

// ─── View ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;

export default function MastersheetView() {
  const { t } = useTranslation();
  const { selectedPeriod, setSelectedPeriod } = useR1DashboardContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<MastersheetSearch>(EMPTY_SEARCH);

  const handleSearchChange = (s: MastersheetSearch) => {
    setSearch(s);
    setCurrentPage(1);
  };

  const { data: periodsData } = useQuery({
    queryKey: ["r1", "reporting-periods-per-fund", "all"],
    queryFn: () => r1Api.getReportingPeriodsPerFund({ mode: "all" }),
    staleTime: 60_000,
  });

  const periodOptions = useMemo(() => {
    if (!periodsData) return [];
    const periods = new Set<string>();
    for (const ps of Object.values(periodsData)) {
      for (const p of ps) periods.add(p);
    }
    return sortPeriods(Array.from(periods)).reverse();
  }, [periodsData]);

  const effectivePeriod = selectedPeriod ?? periodOptions[0] ?? null;

  useEffect(() => {
    if (periodOptions.length === 0) return;
    if (!selectedPeriod || !periodOptions.includes(selectedPeriod)) {
      setSelectedPeriod(periodOptions[0]);
    }
  }, [periodOptions, selectedPeriod, setSelectedPeriod]);

  useEffect(() => {
    setCurrentPage(1);
  }, [effectivePeriod]);

  const { data: posData, isLoading: posLoading } = useQuery({
    queryKey: ["r1", "positions-all", effectivePeriod, currentPage, search],
    queryFn: () =>
      r1Api.getPositions({
        period: effectivePeriod,
        page: currentPage,
        pageSize: PAGE_SIZE,
        companyNameSearch: search.companyName || undefined,
        fundNameSearch: search.fundName || undefined,
        fundGpIdSearch: search.fundGpId || undefined,
        gpIdPcSearch: search.gpIdPc || undefined,
        issuerId: search.issuerId
          ? Number.isFinite(Number(search.issuerId)) ? Number(search.issuerId) : undefined
          : undefined,
      }),
    staleTime: 60_000,
    enabled: !!effectivePeriod,
    placeholderData: keepPreviousData,
  });

  const issuerIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of (posData?.data ?? []) as Record<string, unknown>[]) {
      const id = Number(row.issuer_id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return Array.from(ids).sort((a, b) => a - b);
  }, [posData]);

  const { data: issData } = useQuery({
    queryKey: ["r1", "issuers-by-ids", issuerIds],
    queryFn: () => r1Api.getIssuers({ ids: issuerIds }),
    staleTime: 60_000,
    enabled: issuerIds.length > 0,
  });

  const { data: fundsData } = useQuery({
    queryKey: ["r1", "funds"],
    queryFn: () => r1Api.getFunds(),
    staleTime: 60_000,
  });

  const issuerMap = useMemo(() => {
    const map = new Map<number, Record<string, unknown>>();
    for (const iss of issData?.data ?? []) {
      map.set(Number(iss.id ?? iss.issuer_id), iss as Record<string, unknown>);
    }
    return map;
  }, [issData]);

  const fundMap = useMemo(() => {
    const map = new Map<number, Record<string, unknown>>();
    for (const f of fundsData?.data ?? []) {
      map.set(f.id ?? f.fund_id, f as unknown as Record<string, unknown>);
    }
    return map;
  }, [fundsData]);

  const totalCount = posData?.count ?? posData?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const rows = useMemo(() => {
    return ((posData?.data ?? []) as MastersheetRow[]).map((pos) => {
      const iss = issuerMap.get(Number(pos.issuer_id)) ?? {};
      const fund = fundMap.get(Number(pos.fund_id)) ?? {};
      const row = { ...iss, ...fund, ...pos } as MastersheetRow;
      // r1_positions may have null copies of issuer fields — restore from issuer
      if (iss.old_names != null) row.old_names = iss.old_names;
      const tags = splitTags(row.tags);
      for (let i = 0; i < 8; i += 1) {
        row[`tags_${i + 1}`] = tags[i] ?? "";
      }
      return row;
    });
  }, [posData, issuerMap, fundMap]);

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      {posLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-700">
              Reporting period
            </label>
            {periodOptions.length > 0 ? (
              <Select
                size="sm"
                selectedKeys={
                  effectivePeriod ? new Set([effectivePeriod]) : new Set()
                }
                onSelectionChange={(keys) =>
                  setSelectedPeriod((Array.from(keys)[0] as string) ?? null)
                }
                aria-label="Reporting period"
                className="w-44"
                classNames={{
                  trigger:
                    "h-9 min-h-9 shadow-none !rounded-md border-slate-300 text-sm",
                  value: "text-sm",
                }}
              >
                {periodOptions.map((p) => (
                  <SelectItem key={p}>{p}</SelectItem>
                ))}
              </Select>
            ) : (
              <div className="h-9 min-w-44 rounded-md border border-slate-300 bg-slate-50 px-3 text-sm text-slate-700 flex items-center">
                —
              </div>
            )}
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <R1DataTable<MastersheetRow, unknown>
              table_name="r1-mastersheet-v6"
              label="Mastersheet"
              columns={columns}
              data={rows}
              new_item={<SearchPopover search={search} onChange={handleSearchChange} />}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["fund_name", "company_name_long"]}
              columnVisibilityInit={COLUMN_VISIBILITY_INIT}
              noCard
              fillHeight
            />
            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildMastersheetUrl(effectivePeriod)}
                filename={`r1_mastersheet_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                className="text-sm"
              />
              {pageCount > 1 && (
                <Pagination
                  showControls
                  total={pageCount}
                  page={currentPage}
                  onChange={setCurrentPage}
                  size="sm"
                  radius="md"
                  color="primary"
                />
              )}
              <span className="text-sm text-slate-500">
                {t("table.rowCount", {
                  formatted: rows.length.toLocaleString(),
                })}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
